import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Label } from '../ui/label';
import { HierarchyFilter as HierarchyFilterType, PeriodGranularity, BrandScope } from '../../lib/types';
import { format, subDays, startOfMonth, startOfQuarter, startOfYear } from 'date-fns';

interface HierarchyFilterProps {
  filter: HierarchyFilterType;
  onChange: (filter: HierarchyFilterType) => void;
  onExportCSV?: () => void;
  onExportPNG?: () => void;
  onExportPagePDF?: () => void;
  showBrandScope?: boolean;
}

const PERIOD_PRESETS = [
  { label: 'Last 7 Days', value: '7d' },
  { label: 'Last 30 Days', value: '30d' },
  { label: 'Last 90 Days', value: '90d' },
  { label: 'Month to Date', value: 'mtd' },
  { label: 'Quarter to Date', value: 'qtd' },
  { label: 'Year to Date', value: 'ytd' },
  { label: 'Custom', value: 'custom' },
];

const GRANULARITIES: { label: string; value: PeriodGranularity }[] = [
  { label: 'Hourly', value: 'hour' },
  { label: 'Daily', value: 'day' },
  { label: 'Weekly', value: 'week' },
  { label: 'Monthly', value: 'month' },
  { label: 'Quarterly', value: 'quarter' },
];

const BRAND_SCOPES: { label: string; value: BrandScope }[] = [
  { label: 'Products OR Campaigns', value: 'sku_or_campaign' },
  { label: 'Products Only', value: 'sku_only' },
  { label: 'Campaigns Only', value: 'campaign_only' },
];

export function HierarchyFilterComponent({ 
  filter, 
  onChange, 
  onExportCSV, 
  onExportPNG, 
  onExportPagePDF,
  showBrandScope = false
}: HierarchyFilterProps) {
  const [showCustom, setShowCustom] = useState(false);

  const handlePresetChange = (value: string) => {
    if (value === 'custom') {
      setShowCustom(true);
      return;
    }

    const now = new Date();
    let dateFrom: Date;
    let dateTo = now;

    switch (value) {
      case '7d':
        dateFrom = subDays(now, 7);
        break;
      case '30d':
        dateFrom = subDays(now, 30);
        break;
      case '90d':
        dateFrom = subDays(now, 90);
        break;
      case 'mtd':
        dateFrom = startOfMonth(now);
        break;
      case 'qtd':
        dateFrom = startOfQuarter(now);
        break;
      case 'ytd':
        dateFrom = startOfYear(now);
        break;
      default:
        return;
    }

    onChange({
      ...filter,
      dateFrom: dateFrom.toISOString(),
      dateTo: dateTo.toISOString(),
    });
    setShowCustom(false);
  };

  const handleGranularityChange = (value: PeriodGranularity) => {
    onChange({
      ...filter,
      granularity: value,
    });
  };

  const handleCompareToggle = () => {
    onChange({
      ...filter,
      compareToPrevious: !filter.compareToPrevious,
    });
  };

  const handleBrandScopeChange = (value: BrandScope) => {
    onChange({
      ...filter,
      brandScope: value,
    });
  };

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-wrap items-center gap-4">
          {/* Period Preset */}
          <div className="flex flex-col gap-1">
            <Label className="text-xs">Period</Label>
            <Select onValueChange={handlePresetChange}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Last 30 Days" />
              </SelectTrigger>
              <SelectContent>
                {PERIOD_PRESETS.map(preset => (
                  <SelectItem key={preset.value} value={preset.value}>
                    {preset.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Custom Date Range */}
          {showCustom && (
            <>
              <div className="flex flex-col gap-1">
                <Label className="text-xs">From</Label>
                <input
                  type="date"
                  value={format(new Date(filter.dateFrom), 'yyyy-MM-dd')}
                  onChange={(e) => onChange({ ...filter, dateFrom: new Date(e.target.value).toISOString() })}
                  className="border rounded px-3 py-2 text-sm"
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label className="text-xs">To</Label>
                <input
                  type="date"
                  value={format(new Date(filter.dateTo), 'yyyy-MM-dd')}
                  onChange={(e) => onChange({ ...filter, dateTo: new Date(e.target.value).toISOString() })}
                  className="border rounded px-3 py-2 text-sm"
                />
              </div>
            </>
          )}

          {/* Granularity */}
          <div className="flex flex-col gap-1">
            <Label className="text-xs">Granularity</Label>
            <Select value={filter.granularity} onValueChange={handleGranularityChange}>
              <SelectTrigger className="w-[130px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {GRANULARITIES.map(gran => (
                  <SelectItem key={gran.value} value={gran.value}>
                    {gran.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Operator Hierarchy Filter */}
          <div className="flex flex-col gap-1">
            <Label className="text-xs">Operator</Label>
            <Select value={filter.operatorIds?.[0] || 'all'} onValueChange={(value) => {
              onChange({
                ...filter,
                operatorIds: value === 'all' ? [] : [value],
                siteIds: [], // Reset site filter when operator changes
                machineIds: [] // Reset machine filter when operator changes
              });
            }}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Operators" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Operators</SelectItem>
                <SelectItem value="op1">GymGroup</SelectItem>
                <SelectItem value="op2">Office Solutions</SelectItem>
                <SelectItem value="op3">Campus Vending</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Site Filter (only show if operator is selected) */}
          {filter.operatorIds && filter.operatorIds.length > 0 && (
            <div className="flex flex-col gap-1">
              <Label className="text-xs">Site</Label>
              <Select value={filter.siteIds?.[0] || 'all'} onValueChange={(value) => {
                onChange({
                  ...filter,
                  siteIds: value === 'all' ? [] : [value],
                  machineIds: [] // Reset machine filter when site changes
                });
              }}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All Sites" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sites</SelectItem>
                  <SelectItem value="site1">Central Gym</SelectItem>
                  <SelectItem value="site2">Business Center</SelectItem>
                  <SelectItem value="site3">University Campus</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Machine Filter (only show if site is selected) */}
          {filter.siteIds && filter.siteIds.length > 0 && (
            <div className="flex flex-col gap-1">
              <Label className="text-xs">Machine</Label>
              <Select value={filter.machineIds?.[0] || 'all'} onValueChange={(value) => {
                onChange({
                  ...filter,
                  machineIds: value === 'all' ? [] : [value]
                });
              }}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All Machines" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Machines</SelectItem>
                  <SelectItem value="machine1">Gym-001</SelectItem>
                  <SelectItem value="machine2">Gym-002</SelectItem>
                  <SelectItem value="machine3">Office-001</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Brand Scope (only for brand users) */}
          {showBrandScope && (
            <div className="flex flex-col gap-1">
              <Label className="text-xs">Scope</Label>
              <Select value={filter.brandScope || 'sku_or_campaign'} onValueChange={handleBrandScopeChange}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {BRAND_SCOPES.map(scope => (
                    <SelectItem key={scope.value} value={scope.value}>
                      {scope.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Compare Toggle */}
          <div className="flex items-center gap-2 mt-5">
            <input
              type="checkbox"
              id="compare"
              checked={filter.compareToPrevious || false}
              onChange={handleCompareToggle}
              className="rounded"
            />
            <Label htmlFor="compare" className="text-sm cursor-pointer">
              Compare to Previous
            </Label>
          </div>

          {/* Export Actions */}
          <div className="ml-auto flex gap-2 mt-5">
            {onExportCSV && (
              <Button variant="outline" size="sm" onClick={onExportCSV}>
                Export CSV
              </Button>
            )}
            {onExportPNG && (
              <Button variant="outline" size="sm" onClick={onExportPNG}>
                Export PNG
              </Button>
            )}
            {onExportPagePDF && (
              <Button variant="outline" size="sm" onClick={onExportPagePDF}>
                Export Page PDF
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

